import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';

/**
 * Reusable single-select dropdown component
 */
const SingleSelectDropdown = ({
  label,
  value,
  options = [],
  onChange,
  placeholder = 'Select an option...',
  emptyText = 'No options available',
  className = '',
  disabled = false,
  maxHeight = '16rem',
  allowClear = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectOption = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const getDisplayText = () => {
    if (value == null) return placeholder;
    const option = options.find(opt => opt.value === value);
    return option?.label || value;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="text-xs font-medium text-base-content/60 mb-2 block">
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 bg-base-100 border border-base-300 rounded-lg flex items-center justify-between transition-colors ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:border-base-400 focus:border-primary focus:outline-none'
        }`}
      >
        <span className="text-sm truncate">
          {getDisplayText()}
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && !disabled && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute z-10 mt-1 w-full bg-white border border-base-200 rounded-lg shadow-lg overflow-hidden"
          >
            <div
              className="py-1 overflow-y-auto"
              style={{ maxHeight }}
            >
              {allowClear && value != null && (
                <button
                  onClick={() => handleSelectOption(null)}
                  className="w-full px-3 py-2 text-left hover:bg-base-50 flex items-center justify-between text-sm text-base-content/60 border-b border-base-200"
                >
                  <span>Clear selection</span>
                </button>
              )}

              {options.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-base-content/50">
                  {emptyText}
                </div>
              ) : (
                options.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleSelectOption(option.value)}
                    className={`w-full px-3 py-2 text-left hover:bg-base-50 flex items-center justify-between transition-colors ${
                      value === option.value ? 'bg-blue-50' : ''
                    }`}
                  >
                    <span className="text-sm">
                      {option.label}
                    </span>
                    {value === option.value && (
                      <CheckIcon className="w-4 h-4 text-blue-600" />
                    )}
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SingleSelectDropdown;